import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Plus, Rocket, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import LookupSelect from "@/components/forms/LookupSelect";
import OfferFormPage from "@/pages/dashboard/producer/OfferFormPage";
import { CreateProducerDialog, DeleteConfirmDialog } from "@/pages/admin/UsersPanel";
import { useLookups } from "@/contexts/LookupsContext";
import api, { formatApiErrorDetail } from "@/lib/api";

function useAdminOffers() {
  const [offers, setOffers] = useState(null);
  const reload = () => api.get("/admin/offers").then(({ data }) => setOffers(data));
  useEffect(() => { reload(); }, []); // eslint-disable-line
  return [offers, reload];
}

const BULK_UPDATE_FIELDS = ["region", "country", "housing", "meals", "mission_type", "duration"];
const BULK_UPDATE_FIELD_LABEL_KEY = { region: "region", country: "country", housing: "housing", meals: "meals", mission_type: "missionType", duration: "duration" };

function BulkUpdateOffersDialog({ open, onOpenChange, offerIds, onUpdated }) {
  const { t } = useTranslation();
  const lookups = useLookups();
  const [field, setField] = useState("region");
  const [value, setValue] = useState(null);
  const [saving, setSaving] = useState(false);
  const lookupKeyByField = { region: "regions", country: "countries", housing: "housing_options", meals: "meals_options", mission_type: "mission_types", duration: "durations" };
  const labelPrefixByField = { region: "regions", country: "countries", housing: "housingOptions", meals: "mealsOptions", mission_type: "missionTypes", duration: "durations" };

  const apply = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/offers/bulk-update", { offer_ids: [...offerIds], updates: { [field]: value } });
      toast.success(`${data.modified_count} ${t("admin.bulkActionApplied")}`);
      onOpenChange(false);
      setValue(null);
      onUpdated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setSaving(false);
    }
  };

  if (!lookups) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-md">
        <DialogHeader><DialogTitle>{t("admin.bulkUpdateOffersTitle")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="mb-1.5 block">{t("admin.bulkUpdateFieldLabel")}</Label>
            <Select value={field} onValueChange={(v) => { setField(v); setValue(null); }}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="admin-bulk-update-field-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BULK_UPDATE_FIELDS.map((f) => <SelectItem key={f} value={f}>{t(`offer.${BULK_UPDATE_FIELD_LABEL_KEY[f]}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block">{t("admin.bulkUpdateValueLabel")}</Label>
            <LookupSelect options={lookups[lookupKeyByField[field]]} value={value} onChange={setValue}
                          labelPrefix={`lookups.${labelPrefixByField[field]}`} testId="admin-bulk-update-value-select" />
          </div>
        </div>
        <Button onClick={apply} disabled={saving || !value} className="w-full bg-[#4ba3b7] hover:bg-[#3D8A9A] rounded-xl" data-testid="admin-bulk-update-submit-button">
          {saving ? t("common.loading") : t("common.save")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function CreateOfferDialog({ open, onOpenChange, onCreated }) {
  const { t } = useTranslation();
  const [producers, setProducers] = useState(null);
  const [producerId, setProducerId] = useState(null);
  const [createProducerOpen, setCreateProducerOpen] = useState(false);

  const loadProducers = () => api.get("/admin/users", { params: { role: "producer" } }).then(({ data }) => setProducers(data));
  useEffect(() => { if (open) loadProducers(); }, [open]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("admin.createOfferTitle")}</DialogTitle></DialogHeader>
          <div>
            <Label className="mb-1.5 block">{t("admin.selectProducerLabel")}</Label>
            <div className="flex items-center gap-2">
              <Select value={producerId || undefined} onValueChange={setProducerId}>
                <SelectTrigger className="h-11 rounded-xl flex-1" data-testid="admin-create-offer-producer-select"><SelectValue placeholder={t("admin.selectProducerLabel")} /></SelectTrigger>
                <SelectContent>
                  {producers?.map((p) => <SelectItem key={p.id} value={p.id}>{p.display_name || p.email}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="button" size="sm" variant="outline" className="rounded-xl gap-1.5 shrink-0" onClick={() => setCreateProducerOpen(true)} data-testid="admin-create-offer-new-producer-button">
                <Plus className="h-3.5 w-3.5" /> {t("admin.newProducer")}
              </Button>
            </div>
          </div>
          {producerId && (
            <OfferFormPage producerId={producerId} onSaved={() => { onOpenChange(false); setProducerId(null); onCreated(); }} />
          )}
        </DialogContent>
      </Dialog>
      <CreateProducerDialog open={createProducerOpen} onOpenChange={setCreateProducerOpen} onCreated={() => loadProducers()} />
    </>
  );
}

function BulkOfferToolbar({ selectedIds, onDone, onOpenBulkUpdate }) {
  const { t } = useTranslation();
  const [running, setRunning] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const runAction = async (action) => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/offers/bulk-action", { offer_ids: [...selectedIds], action });
      toast.success(`${data.modified_count} ${t("admin.bulkActionApplied")}`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setRunning(false);
    }
  };

  const runBulkDelete = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/offers/bulk-delete", { offer_ids: [...selectedIds] });
      toast.success(`${data.deleted_count} ${t("admin.bulkDeleteOffersApplied")}`);
      setConfirmDeleteOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap bg-[#2B2B2B] text-white rounded-xl p-3" data-testid="admin-offers-bulk-toolbar">
      <span className="text-sm font-medium px-2">{selectedIds.size} {t("admin.selected")}</span>
      <Button size="sm" variant="secondary" disabled={running} onClick={() => runAction("boost")} data-testid="admin-bulk-boost-button">{t("admin.boostOffer")}</Button>
      <Button size="sm" variant="secondary" disabled={running} onClick={() => runAction("deactivate")} data-testid="admin-bulk-deactivate-button">{t("offer.closeOffer")}</Button>
      <Button size="sm" variant="secondary" disabled={running} onClick={onOpenBulkUpdate} data-testid="admin-open-bulk-update-offers">{t("admin.bulkUpdateOffersTitle")}</Button>
      <Button size="sm" variant="destructive" disabled={running} onClick={() => setConfirmDeleteOpen(true)} data-testid="admin-bulk-delete-offers-button">
        <Trash2 className="h-3.5 w-3.5 mr-1" /> {t("admin.bulkDeleteButton")}
      </Button>
      <DeleteConfirmDialog
        open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen} confirming={running}
        title={t("admin.bulkDeleteOffersConfirmTitle", { count: selectedIds.size })}
        description={t("admin.bulkDeleteOffersConfirmDesc")}
        onConfirm={runBulkDelete} testId="admin-bulk-delete-offers-confirm-dialog"
      />
    </div>
  );
}

export default function OffersPanel() {
  const { t } = useTranslation();
  const [offers, reload] = useAdminOffers();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => setSelectedIds((prev) => (prev.size === offers.length ? new Set() : new Set(offers.map((o) => o.id))));

  const deactivate = async (id) => { await api.post(`/admin/offers/${id}/deactivate`); reload(); };
  const boost = async (id) => {
    try {
      await api.post(`/admin/offers/${id}/boost`);
      toast.success(t("admin.offerBoostSuccess"));
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/offers/${deleteTarget.id}`);
      toast.success(t("common.success"));
      setDeleteTarget(null);
      setSelectedIds((prev) => { const next = new Set(prev); next.delete(deleteTarget.id); return next; });
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setDeleting(false);
    }
  };

  if (!offers) return <Loader2 className="h-6 w-6 animate-spin text-[#4ba3b7] mx-auto" />;

  return (
    <div className="space-y-4" data-testid="admin-offers-panel">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" className="rounded-xl gap-1.5" onClick={() => setCreateOpen(true)} data-testid="admin-open-create-offer">
          <Plus className="h-3.5 w-3.5" /> {t("admin.createOfferTitle")}
        </Button>
      </div>

      {selectedIds.size > 0 && (
        <BulkOfferToolbar selectedIds={selectedIds} onDone={() => { setSelectedIds(new Set()); reload(); }} onOpenBulkUpdate={() => setBulkUpdateOpen(true)} />
      )}

      {offers.length > 0 && (
        <div className="flex items-center gap-2 px-4 text-xs text-[#2B2B2B]/50">
          <Checkbox checked={selectedIds.size === offers.length} onCheckedChange={toggleSelectAll} data-testid="admin-offers-select-all" />
          {t("admin.selectAll")}
        </div>
      )}

      <div className="space-y-2">
        {offers.map((o) => (
          <div key={o.id} className="flex items-center justify-between bg-white rounded-xl border border-black/5 p-4 text-sm gap-2 flex-wrap" data-testid={`admin-offer-row-${o.id}`}>
            <div className="flex items-center gap-3">
              <Checkbox checked={selectedIds.has(o.id)} onCheckedChange={() => toggleSelect(o.id)} data-testid={`admin-offer-select-${o.id}`} />
              <div>
                <span>{t(`lookups.missionTypes.${o.mission_type}`)} &middot; {t(`lookups.regions.${o.region}`)}</span>
                {o.producer_company_name && <span className="text-xs text-[#2B2B2B]/50 block">{o.producer_company_name}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{o.status}</Badge>
              {o.is_boosted && <Badge className="bg-[#dde165]/30 border-0 text-[#2B2B2B]">{t("admin.boostedBadge")}</Badge>}
              {!o.is_boosted && (
                <Button size="sm" variant="ghost" className="gap-1.5 text-[#4ba3b7]" onClick={() => boost(o.id)} data-testid={`admin-boost-offer-${o.id}`}>
                  <Rocket className="h-3.5 w-3.5" /> {t("admin.boostOffer")}
                </Button>
              )}
              {o.status === "active" && <Button size="sm" variant="ghost" className="text-[#FF3B30]" onClick={() => deactivate(o.id)} data-testid={`admin-deactivate-offer-${o.id}`}>{t("offer.closeOffer")}</Button>}
              <Button size="sm" variant="ghost" className="text-[#FF3B30]" title={t("admin.deleteOffer")} onClick={() => setDeleteTarget(o)} data-testid={`admin-delete-offer-${o.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <CreateOfferDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={reload} />
      <BulkUpdateOffersDialog open={bulkUpdateOpen} onOpenChange={setBulkUpdateOpen} offerIds={selectedIds}
                              onUpdated={() => { setSelectedIds(new Set()); setBulkUpdateOpen(false); reload(); }} />
      <DeleteConfirmDialog
        open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)} confirming={deleting}
        title={t("admin.deleteOfferConfirmTitle")}
        description={deleteTarget ? `${t(`lookups.missionTypes.${deleteTarget.mission_type}`)} \u00b7 ${t(`lookups.regions.${deleteTarget.region}`)} (${deleteTarget.producer_company_name || ""}) — ${t("admin.deleteOfferConfirmDesc")}` : ""}
        onConfirm={confirmDelete} testId="admin-delete-offer-confirm-dialog"
      />
    </div>
  );
}
